import axios, { AxiosRequestConfig, Method } from 'axios';
import moment from 'moment';

import { USER_AUTH_TICKET } from 'src/reducers/auth/types';

// import { REDIRECT_MAINTENANCE } from 'Constants/actionTypes';
import { _config } from 'Utilities/envConfig';
import * as log from 'Utilities/log';
import mediator from 'Utilities/mediator';
import { _handleMockMessage } from 'Utilities/newyu';
import {
  getCookie,
  getObject,
  getSessionObject,
  setCookie,
  setSessionObject,
} from 'Utilities/storage';
import { store } from 'Utilities/store';

const _authTicket: AuthTicket = {
  expires: 0,
  token: '',
};
let _ticketTimeout = 0;

// Try to get our token from session storage first or fallback to cookies
try {
  const sessionToken = getSessionObject('token');

  if (sessionToken) {
    _authTicket.duration = sessionToken.duration;
    _authTicket.expires = sessionToken.expires;
    _authTicket.token = sessionToken.token;
  }

  if (_authTicket.token === '') {
    const cookieToken = getCookie('token');

    if (cookieToken) {
      _authTicket.expires = moment().add(2, 'minute').unix();
      _authTicket.token = cookieToken;
    }
  }
} catch (e) {
  const cookieToken = getCookie('token');

  if (cookieToken) {
    _authTicket.expires = moment().add(2, 'minute').unix();
    _authTicket.token = cookieToken;
  }
}

/**
 * this is the proper api service which for now isn't really implemented
 * when in dev (during the MOCK times) the dev webpack will change imports for
 * this to the one in the mock folder
 *
 * TODO: make this encode the url for us
 */

// Log out user
const _handleLogOut = () => {
  setSessionObject('loggedOut', moment().unix());
  mediator.publish('user:loggedOut');
};

// Setup a timeout to forcibly log the user out when the session is expected to expire
const _handleExpireTimeout = () => {
  window.clearTimeout(_ticketTimeout);

  // Already expired
  if (_authTicket.expires === 0 || _authTicket.duration === 0) {
    return;
  }

  _ticketTimeout = window.setTimeout(_handleLogOut, _authTicket.duration);
};

const persistToken = (token: AuthTicket) => {
  try {
    setSessionObject('token', token);
    setCookie('token', token.token, 7200000);
  } catch (e) {
    setCookie('token', token.token, 7200000);
  }
};

// NOTE: replaced fetch with axios which is more standard and
// not plagued with some of the cross browser issues with fetch
const _invoke = (method: Method, url: string, data: any, query: any) => {
  return new Promise((resolve, reject) => {
    const request: AxiosRequestConfig = {
      method: method,
      url: url,
      data: data,
      params: query,
    };

    request.headers = {
      'Cache-control': 'no-cache',
      Pragma: 'no-cache',
      'Sharing-Web-Version': process.env.VERSION,
    };

    const secret = getObject('NEWYU_OPEN');

    if (secret) {
      request.headers['newyu-open'] = secret;
    }

    if (_authTicket.token && _authTicket.token !== 'LOGGEDOUT') {
      request.headers.Authorization = 'Bearer ' + _authTicket.token;
    }

    axios(request)
      .then((response) => {
        if (log.getLogLevel() === 'DEBUG') {
          log.groupAndTrace(
            `API: response for ${url}`,
            ['request', log.cleanObjectProperties(request)],
            ['response', response]
          );
        }

        if (!response.data) {
          // TODO: look into rejecting here based on what is done in sharing web eventually
          resolve(response.data);

          return;
        }

        const responseData: LVApiResponse<{ authTicket?: AuthTicket }> = response.data;

        if (responseData && responseData.data && responseData.data.authTicket) {
          _authTicket.duration = responseData.data.authTicket.duration;
          _authTicket.expires = responseData.data.authTicket.expires;
          _authTicket.token = responseData.data.authTicket.token;
          _handleExpireTimeout();
          persistToken(_authTicket);
          store.dispatch({ type: USER_AUTH_TICKET, token: _authTicket.token });
        } else if (responseData.ticket) {
          _authTicket.duration = responseData.ticket.duration;
          _authTicket.expires = responseData.ticket.expires;
          _authTicket.token = responseData.ticket.token;
          _handleExpireTimeout();
          persistToken(_authTicket);
          store.dispatch({ type: USER_AUTH_TICKET, token: _authTicket.token });
        }

        resolve(responseData);
      })
      .catch((error) => {
        if (log.getLogLevel() === 'DEBUG') {
          log.groupAndTrace(
            `API: Error response for ${url}`,
            ['request', log.cleanObjectProperties(request)],
            ['response', error]
          );
        }

        if (error.response) {
          // request was made but server responded with code that was not in the
          // range of 2xx
          if (error.response.status === 911) {
            // we were in a valid session but maintenance just began
            reject(error);
          } else {
            resolve({ status: error.response.status });
          }
        } else if (error.request) {
          // request was made, but no response was ever received
          reject({ status: 998 });
        } else {
          reject({ status: 999 });
        }
      });
  });
};

const _handleInvoke = (request: any) => {
  if (log.getLogLevel() === 'DEBUG') {
    log.debug('API: incomingRequest:' + request.url, log.cleanObjectProperties(request));
  }

  if (!request) {
    throw new Error('missing Request');
  }

  if (_config.mock) {
    mediator.request(_handleMockMessage('api:invoke'), request);
    return;
  }

  // validate the request
  try {
    if (!request.method) {
      throw new Error('missing method(GET,POST,PUT,DELETE)');
    }

    if (!request.url) {
      throw new Error('missing url(users/create)');
    }

    request.response = _invoke(request.method, request.url, request.data, request.query);
  } catch (ex) {
    request.response = new Promise((_resolve, reject) => {
      reject(ex);
    });
  }
};

const _handleTicket = (_id: string, request: any) => {
  request.ticket = _authTicket;
};

const _setTicket = () => {
  const ticket = getSessionObject('token');

  if (ticket) {
    _authTicket.duration = ticket.duration;
    _authTicket.expires = ticket.expires;
    _authTicket.token = ticket.token;
  }

  mediator.publish('app:queryTokenRead');
};

mediator.subscribe('api:invoke', _handleInvoke);
mediator.subscribe('api:ticket', _handleTicket);
mediator.subscribe('api:setTicket', _setTicket);
